import { ComponentConfig } from "@puckeditor/core";
import Image from "next/image";
import { ImagePicker } from "@/components/ImagePicker";
import { sharedFields } from "../fields/shared";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface InlineImageProps {
    imageUrl: string;
    caption?: string;
    altText?: string;
    size?: "small" | "medium" | "large" | "full";
    aspectRatio?: "video" | "landscape" | "square" | "portrait";
    objectPosition?: "center" | "top" | "bottom" | "left" | "right";
}

export const InlineImageBlockConfig: ComponentConfig<InlineImageProps> = {
    fields: {
        imageUrl: {
            type: "custom",
            render: ({ value, onChange }: any) => (
                <ImagePicker value={value} onChange={onChange} />
            )
        },
        caption: { type: "text" },
        altText: { type: "text", label: "Alternative Text" },
        size: {
            type: "select",
            options: [
                { label: "Small", value: "small" },
                { label: "Medium", value: "medium" },
                { label: "Large", value: "large" },
                { label: "Full Width", value: "full" },
            ]
        },
        aspectRatio: {
            type: "select",
            label: "Image Shape",
            options: [
                { label: "Wide 16:9", value: "video" },
                { label: "Landscape 4:3", value: "landscape" },
                { label: "Square 1:1", value: "square" },
                { label: "Portrait 4:5", value: "portrait" },
            ],
        },
        objectPosition: {
            type: "select",
            label: "Crop Focus",
            options: [
                { label: "Center", value: "center" },
                { label: "Top", value: "top" },
                { label: "Bottom", value: "bottom" },
                { label: "Left", value: "left" },
                { label: "Right", value: "right" },
            ],
        },
    },
    defaultProps: {
        imageUrl: "/og-lab-v2.jpg",
        caption: "Shaaira Biocare formulation and manufacturing workflow.",
        altText: "Cosmetic formulation workflow in Shaaira Biocare's laboratory",
        size: "large",
        aspectRatio: "landscape",
        objectPosition: "center",
    },
    render: ({ imageUrl, caption, altText, size, aspectRatio = "landscape", objectPosition = "center" }) => {
        if (!imageUrl) {
            return (
                <div className="w-full bg-slate-100 rounded-2xl flex items-center justify-center p-12 text-slate-400 border-2 border-dashed border-slate-200">
                    <span className="font-semibold text-sm">Select an Image</span>
                </div>
            );
        }

        const sizeClasses = {
            small: "max-w-md",
            medium: "max-w-2xl",
            large: "max-w-4xl",
            full: "max-w-full rounded-none"
        };
        const aspectClasses = {
            video: "aspect-video",
            landscape: "aspect-[4/3]",
            square: "aspect-square",
            portrait: "aspect-[4/5]",
        };
        const positionClasses = {
            center: "object-center",
            top: "object-top",
            bottom: "object-bottom",
            left: "object-left",
            right: "object-right",
        };

        return (
            <figure className={`mx-auto w-full flex flex-col items-center my-10 ${sizeClasses[size || "large"]}`}>
                <div className={`relative w-full ${aspectClasses[aspectRatio]} overflow-hidden ${size === 'full' ? '' : 'rounded-2xl'}`}>
                    <Image
                        src={resolveMediaUrl(imageUrl, "/og-lab-v2.jpg")}
                        alt={altText || caption || "Blog Image"}
                        fill
                        className={`object-cover ${positionClasses[objectPosition]}`}
                        sizes="(max-width: 1024px) 100vw, 800px"
                    />
                </div>
                {caption && (
                    <figcaption className="mt-4 text-center text-sm font-medium text-slate-500 max-w-2xl px-4">
                        {caption}
                    </figcaption>
                )}
            </figure>
        );
    }
};
